import jsPDF from 'jspdf';
import dayjs from 'dayjs';

export function generateEvaluationPdf(evaluationReport, person, academicYear) {
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const margin = 40;
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  let y = margin;
  const primaryColor = [255, 165, 0];

  //HEADER
  doc.setFontSize(16);
  doc.setTextColor(...primaryColor);
  doc.setFont(undefined, 'bold');
  const titleLines = ['Administrator Evaluation', 'Summative Report'];
  doc.text(titleLines, margin, y);

  // USER INFO
  const infoLines = [
    `Administrator: ${person?.lastName}, ${person?.firstName}`,
    `Evaluation: ${academicYear?.name || 'N/A'} Summative`,
    `Evaluator: ${evaluationReport?.evaluator?.lastName}, ${evaluationReport?.evaluator?.firstName}`,
  ];
  doc.setFont(undefined, 'normal');
  doc.setFontSize(10);
  doc.setTextColor(0);
  doc.text(infoLines, pageWidth - margin, y, {
    align: 'right',
    lineHeightFactor: 1.1
  });

  //Calculate vertical space
  const titleBlockHeight = titleLines.length * 16 * 1.15;
  const infoBlockHeight = infoLines.length * 10 * 1.1;
  y += Math.max(titleBlockHeight, infoBlockHeight) + 2;

  // Bottom Border
  doc.setDrawColor(...primaryColor);
  doc.setLineWidth(1.5);
  doc.line(margin, y - 4, pageWidth - margin, y - 4);
  y += 5;

  //PERFORMANCE STANDARDS
  const performanceStandards = [
    'Instructional Leadership',
    'School Climate',
    'Human Resources Management',
    'Organizational Management',
    'Communication and Community Relations',
    'Professionalism'
  ];
  const scoreMap = (evaluationReport?.scores?.standards ?? []).reduce(
    (acc, { standardId, score }) => {
      acc[standardId] = score;
      return acc;
    },
    {}
  );

  const goalMap = (evaluationReport?.scores?.goals ?? []).reduce((acc, { standardId, score, weight }) => {
    acc[standardId] = { score, weight };
    return acc;
  }, {});

  const psTableX = margin;
  const psTableW = pageWidth - margin * 2;
  const psCol1W = psTableW * 0.75;
  const psCol2W = psTableW - psCol1W;
  const psHeaderH = 20;
  const psRowH = 18;
  const psPad = 6;

  doc.setFillColor(255, 240, 220);
  doc.rect(psTableX, y, psCol1W, psHeaderH, 'F');
  doc.rect(psTableX + psCol1W, y, psCol2W, psHeaderH, 'F');

  doc.setFontSize(10);
  doc.setTextColor(...primaryColor);
  doc.setFont(undefined, 'bold');
  doc.text('Performance Standards', psTableX + psPad, y + psHeaderH - psPad);
  doc.text('Score', psTableX + psTableW - psPad, y + psHeaderH - psPad, { align: 'right' });

  y += psHeaderH;

  doc.setFont(undefined, 'normal');
  doc.setTextColor(0);
  performanceStandards.forEach((std, idx) => {
    const rowY = y + idx * psRowH;
    doc.setDrawColor(200);
    doc.line(psTableX, rowY, psTableX + psTableW, rowY);
    doc.text(`Performance Standard ${idx + 1}: ${std}`, psTableX + psPad, rowY + psRowH - psPad);
    const sc = scoreMap[idx + 1] ?? '-';
    doc.text(String(sc), psTableX + psTableW - psPad, rowY + psRowH - psPad, { align: 'right' });
  });

  y += performanceStandards.length * psRowH;
  doc.line(psTableX, y, psTableX + psTableW, y);
  y += 10;

  // SUMMATIVE SCORE TABLE
  const sumTableX = margin;
  const sumTableY = y;
  const sumTableW = pageWidth - margin * 2;

  const sumCol1W = sumTableW * 0.25;
  const sumCol2W = sumTableW * 0.45;
  const sumCol3W = sumTableW - sumCol1W - sumCol2W;
  const sumSubColW = sumCol3W / 2; 

  const sumHeaderH = 20;
  const sumSubHeaderH = 20;
  const sumRowH = 18;
  const sumHeaderPad = 6;

  doc.setFillColor(255, 240, 220);
  doc.rect(sumTableX, y, sumCol1W, sumHeaderH, 'F');
  doc.rect(sumTableX + sumCol1W, y, sumCol2W, sumHeaderH, 'F');
  doc.setFontSize(10);
  doc.setTextColor(...primaryColor);
  doc.setFont(undefined, 'bold');
  doc.text('Summative Score', sumTableX + sumHeaderPad, y + sumHeaderH - sumHeaderPad);

  y += sumHeaderH;

  doc.setFillColor(255, 240, 220);
  // Header spans
  doc.rect(sumTableX, sumTableY, sumCol1W, sumHeaderH + sumSubHeaderH, 'F');
  doc.rect(sumTableX + sumCol1W, sumTableY, sumCol2W, sumHeaderH + sumSubHeaderH, 'F');
  doc.rect(sumTableX + sumCol1W + sumCol2W, sumTableY, sumCol3W, sumHeaderH, 'F');

  // Sub-columns under Standard 7
  doc.rect(
    sumTableX + sumCol1W + sumCol2W,
    sumTableY + sumHeaderH,
    sumSubColW,
    sumSubHeaderH,
    'F'
  );
  doc.rect(
    sumTableX + sumCol1W + sumCol2W + sumSubColW,
    sumTableY + sumHeaderH,
    sumSubColW,
    sumSubHeaderH,
    'F'
  );

  // Outlines
  doc.setDrawColor(0);
  doc.setLineWidth(0.5);
  doc.rect(sumTableX, sumTableY, sumCol1W, sumHeaderH + sumSubHeaderH);
  doc.rect(sumTableX + sumCol1W, sumTableY, sumCol2W, sumHeaderH + sumSubHeaderH);
  doc.rect(sumTableX + sumCol1W + sumCol2W, sumTableY, sumCol3W, sumHeaderH);
  doc.rect(
    sumTableX + sumCol1W + sumCol2W,
    sumTableY + sumHeaderH,
    sumSubColW,
    sumSubHeaderH
  );
  doc.rect(
    sumTableX + sumCol1W + sumCol2W + sumSubColW,
    sumTableY + sumHeaderH,
    sumSubColW,
    sumSubHeaderH
  );

  // Header labels
  const combinedHeaderH = sumHeaderH + sumSubHeaderH;
  const centerBaseline = sumTableY + combinedHeaderH / 2 + 4;
  doc.setFontSize(10);
  doc.setTextColor(...primaryColor);
  doc.setFont(undefined, 'bold');
  doc.text('Scoring', sumTableX + sumHeaderPad, centerBaseline);
  const performancePercentage = evaluationReport.scores.standardPercentage;
  doc.text(
    ['Performance Standards (1 - 6)', `${performancePercentage}%`],
    sumTableX + sumCol1W + sumCol2W / 2,
    centerBaseline - 6,
    { align: 'center', lineHeightFactor: 1 }
  );
  doc.text(
    'Standard 7',
    sumTableX + sumCol1W + sumCol2W + sumCol3W / 2,
    sumTableY + sumHeaderH - sumHeaderPad,
    { align: 'center' }
  );

  // First data row
  y = sumTableY + combinedHeaderH;
  const row1Y = y;
  doc.rect(sumTableX, row1Y, sumCol1W, sumRowH);
  doc.rect(sumTableX + sumCol1W, row1Y, sumCol2W, sumRowH);
  doc.rect(
    sumTableX + sumCol1W + sumCol2W,
    row1Y,
    sumSubColW,
    sumRowH
  );
  doc.rect(
    sumTableX + sumCol1W + sumCol2W + sumSubColW,
    row1Y,
    sumSubColW,
    sumRowH
  );

  // “Effectiveness Score”
  doc.setFontSize(10);
  doc.setTextColor(0);
  doc.setFont(undefined, 'normal');
  doc.text(
    'Effectiveness Score',
    sumTableX + sumHeaderPad,
    row1Y + sumRowH - sumHeaderPad
  );
  const overallScore = evaluationReport?.scores?.overallScore ?? '-';
  const overallScoreLabel = evaluationReport?.scores?.scoreScale?.title;
  doc.text(
    `${overallScore}`,
    sumTableX + sumCol1W + sumCol2W / 2,
    row1Y + sumRowH - sumHeaderPad,
    { align: 'center' }
  );

  // STAR & Admin Goals
  const subY = sumTableY + sumHeaderH + 8;
  const iReadyPercentage = goalMap[7]?.weight;
  const adminPercentage = goalMap[8]?.weight;
  doc.setFontSize(8)
  doc.text(
    ['iReady Goal', `${iReadyPercentage}%`],
    sumTableX + sumCol1W + sumCol2W + sumSubColW * 0.5,
    subY,
    { align: 'center', lineHeightFactor: 1 }
  );

  doc.text(
    ['Admin Goal', `${adminPercentage}%`],
    sumTableX + sumCol1W + sumCol2W + sumSubColW * 1.5,
    subY,
    { align: 'center', lineHeightFactor: 1 }
  );

  doc.setFontSize(10);
  doc.text(
    `${goalMap[7]?.score ?? '-'}`,
    sumTableX + sumCol1W + sumCol2W + sumSubColW * 0.5,
    row1Y + sumRowH - sumHeaderPad,
    { align: 'center', lineHeightFactor: 1 }
  );

  doc.text(
    `${goalMap[8]?.score ?? '-'}`,
    sumTableX + sumCol1W + sumCol2W + sumSubColW * 1.5,
    row1Y + sumRowH - sumHeaderPad,
    { align: 'center', lineHeightFactor: 1 }
  );

  // Second data row (overall)
  y += sumRowH;
  const row2Y = y;
  doc.rect(sumTableX, row2Y, sumCol1W, sumRowH);
  const mergedX = sumTableX + sumCol1W;
  const mergedW = sumCol2W + sumCol3W;
  doc.rect(mergedX, row2Y, mergedW, sumRowH);
  doc.setFont(undefined, 'bold');
  doc.text(
    'Overall Evaluation Score',
    sumTableX + sumHeaderPad,
    row2Y + sumRowH - sumHeaderPad
  );
  const overallLabel = evaluationReport?.scores?.standardAverage ?? '-';
  const centerX = mergedX + mergedW / 2;
  doc.text(
    `${overallLabel} - ${overallScoreLabel}`,
    centerX,
    row2Y + sumRowH - sumHeaderPad,
    { align: 'center' }
  );

  y += sumRowH + 10;
  doc.setFillColor(255, 240, 220);
  doc.rect(psTableX, y, psCol1W, psHeaderH, 'F');
  doc.rect(psTableX + psCol1W, y, psCol2W, psHeaderH, 'F');

  doc.setFontSize(10);
  doc.setTextColor(...primaryColor);
  doc.setFont(undefined, 'bold');
  doc.text('Final Feedback', psTableX + psPad, y + psHeaderH - psPad);
  y += 35;
  doc.setFont(undefined, 'normal');
  const feedback = evaluationReport?.finalFeedback || 'No feedback provided.';
  const feedbackLines = doc.splitTextToSize(feedback, pageWidth - margin * 2.5);
  
  doc.setFontSize(10);
  doc.setTextColor(0);
  doc.text(feedbackLines, margin + 5, y, {
    maxWidth: pageWidth - margin * 2,
    lineHeightFactor: 1.15
  });


  // Footer
  y = pageHeight - 20;
  doc.setFontSize(8);
  doc.setTextColor(150);
  doc.text(
    `Generated on ${dayjs().format('MMMM DD, YYYY h:mm A')}`,
    margin,
    y
  );

  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  window.open(pdfUrl, '_blank');
}
